import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, User as UserIcon, ClockClockwise, Download } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSelection } from "@/hooks/useSelection";
import { useConfirm } from "@/hooks/useConfirm";
import BulkActionBar, { SelectAllCheckbox, RowCheckbox, runBulkDelete, BulkBarButton } from "@/components/BulkActionBar";
import { exportRowsToCsv, todayStamp } from "@/lib/csvExport";

const empty = {
  email: "",
  password: "",
  name: "",
  role: "cajero",
  sucursal_id: "",
};

export default function Usuarios({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  // Only "other" users are bulk-selectable — you can't delete yourself.
  const selectableItems = useMemo(
    () => items.filter((u) => u.id !== currentUser?.id),
    [items, currentUser?.id],
  );
  const selection = useSelection(selectableItems);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const onBulkDelete = async () => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const ok = await confirm({
      title: `Eliminar ${ids.length} usuario${ids.length === 1 ? "" : "s"}`,
      description: "Los usuarios eliminados no podrán iniciar sesión. Sus ventas y movimientos históricos se conservarán para auditoría.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkDelete(ids, (id) => api.delete(`/users/${id}`));
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} usuario(s) eliminado(s)`);
    if (fail) toast.error(`${fail} no se pudieron eliminar`);
    load();
  };

  const onBulkExport = () => {
    const ids = new Set(selection.selectedIds);
    const rows = selectableItems.filter((u) => ids.has(u.id));
    if (!rows.length) return;
    exportRowsToCsv({
      filename: `usuarios-${todayStamp()}.csv`,
      headers: [
        { key: "email", label: "Email" },
        { key: "name", label: "Nombre" },
        { key: "role", label: "Rol" },
        {
          label: "Sucursal",
          value: (u) => sucursales.find((s) => s.id === u.sucursal_id)?.nombre || "",
        },
      ],
      rows,
    });
    toast.success(`${rows.length} usuario(s) exportado(s) a CSV`);
  };

  const viewHistory = (u) => {
    const params = new URLSearchParams({
      entity: "users",
      entity_id: u.id,
      label: u.email || u.name || "",
    });
    navigate(`/auditoria?${params.toString()}`);
  };

  const viewActivity = (u) => {
    const params = new URLSearchParams({
      user_id: u.id,
    });
    navigate(`/auditoria?${params.toString()}`);
  };

  const load = useCallback(async () => {
    try {
      const [u, s, r] = await Promise.all([
        api.get("/users"),
        api.get("/sucursales"),
        api.get("/roles"),
      ]);
      setItems(u.data);
      setSucursales(s.data);
      setRoles(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return toast.error("Email requerido");
    if (!editingId && !form.password) return toast.error("Contraseña requerida");
    setLoading(true);
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          role: form.role,
          sucursal_id: form.sucursal_id || null,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", {
          ...form,
          sucursal_id: form.sucursal_id || null,
        });
        toast.success("Usuario creado");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role || "cajero",
      sucursal_id: u.sucursal_id || "",
    });
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={embedded ? "" : "px-4 sm:px-8 lg:px-16 py-8 max-w-[1100px] mx-auto"}>
      {!embedded && (
        <header className="mb-8">
          <div className="overline text-klein">Configuración</div>
          <h1 className="h-display text-4xl font-black tracking-tighter">Usuarios</h1>
          <p className="text-sm text-muted2 mt-2 max-w-xl">
            Crea cuentas para empleados y cajeros. Asigna roles y sucursal para que sus ventas se registren con su nombre.
          </p>
        </header>
      )}

      <section className="border border-zinc-200 p-6 mb-8" data-testid="user-form-section">
        <div className="flex items-center gap-2 mb-4">
          <UserIcon size={18} className="text-klein" />
          <div className="text-sm font-medium">
            {editingId ? "Editar usuario" : "Nuevo usuario"}
          </div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            testid="user-email"
            disabled={!!editingId}
            required
            help="Email único con el que el cajero/empleado iniciará sesión. No se puede cambiar después de creado."
          />
          <Input
            label={editingId ? "Nueva contraseña (opcional)" : "Contraseña"}
            type="password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            testid="user-password"
            required={!editingId}
            minLength={6}
            help={editingId ? "Si quieres cambiar la contraseña, escribe la nueva (mín. 6). Déjalo en blanco para no cambiarla." : "Contraseña inicial (mín. 6 caracteres). El usuario podrá cambiarla más tarde."}
          />
          <Input
            label="Nombre"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            testid="user-name"
            help="Cómo aparecerá en reportes y junto a sus ventas (ej: 'Ana López')."
          />
          <div>
            <label className="overline block mb-1">Rol</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="user-role"
              data-help="Define qué puede hacer el usuario. Crea roles personalizados desde Ajustes → Roles & Permisos."
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
            >
              {roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}{r.builtin ? "" : " ✦"}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="overline block mb-1">Sucursal asignada</label>
            <select
              value={form.sucursal_id}
              onChange={(e) => setForm({ ...form, sucursal_id: e.target.value })}
              data-testid="user-sucursal"
              data-help="Sucursal donde trabaja este usuario. Sus ventas y vistas quedarán acotadas a esta sucursal automáticamente."
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">Sin asignar</option>
              {sucursales.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="user-submit-btn"
              data-help={editingId ? "Guarda los cambios del usuario." : "Crea el usuario y le permite iniciar sesión con el email y contraseña configurados."}
              className="bg-klein text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-klein/90 disabled:opacity-60"
            >
              <Plus size={16} /> {editingId ? "Guardar cambios" : "Crear usuario"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(empty);
                }}
                data-help="Descarta los cambios y vuelve a 'crear nuevo usuario'."
                className="border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </section>

      <BulkActionBar
        count={selection.count}
        noun="usuario"
        pluralNoun="usuarios"
        onDelete={onBulkDelete}
        onClear={selection.clear}
        busy={bulkBusy}
        testid="users-bulk-bar"
        extraActions={
          <BulkBarButton
            onClick={onBulkExport}
            icon={Download}
            label="Exportar CSV"
            testid="users-bulk-bar-export"
            disabled={bulkBusy}
          />
        }
      />
      {confirmDialog}

      <section className="border border-zinc-200">
        <div className="px-4 py-3 border-b border-zinc-200 text-sm font-medium flex items-center gap-3">
          {selectableItems.length > 0 && (
            <SelectAllCheckbox
              allSelected={selection.allSelected}
              someSelected={selection.someSelected}
              onChange={selection.toggleAll}
              testid="users-select-all"
            />
          )}
          <span>{items.length} usuarios</span>
        </div>
        <div className="divide-y divide-zinc-100">
          {items.map((u) => {
            const suc = sucursales.find((s) => s.id === u.sucursal_id);
            const isSelf = u.id === currentUser?.id;
            return (
              <div
                key={u.id}
                className={`px-4 py-3 flex items-center justify-between gap-3 ${selection.isSelected(u.id) ? "bg-[#EEF2FF]" : ""}`}
                data-testid={`user-row-${u.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isSelf ? (
                    <span className="w-4 h-4 inline-block" title="No puedes eliminarte a ti mismo" />
                  ) : (
                    <RowCheckbox
                      checked={selection.isSelected(u.id)}
                      onChange={() => selection.toggle(u.id)}
                      testid={`select-user-${u.id}`}
                    />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium">
                      {u.name || u.email}{" "}
                      <span className="text-xs px-2 py-0.5 ml-2 bg-zinc-100 rounded">
                        {u.role}
                      </span>
                      {isSelf && (
                        <span className="text-[10px] uppercase tracking-[0.15em] ml-2 text-klein">tú</span>
                      )}
                    </div>
                    <div className="text-xs text-muted2">
                      {u.email}
                      {suc ? ` · ${suc.nombre}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => viewActivity(u)}
                    className="border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 flex items-center gap-1"
                    data-testid={`user-activity-${u.id}`}
                    data-help="Ver TODO lo que ha hecho este usuario en el sistema (productos creados, ventas, logins, etc.)."
                  >
                    <ClockClockwise size={12} /> Actividad
                  </button>
                  <button
                    onClick={() => viewHistory(u)}
                    className="border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 flex items-center gap-1"
                    data-testid={`user-history-${u.id}`}
                    data-help="Ver historial de cambios al PERFIL de este usuario (creación, edición, contraseña)."
                  >
                    <ClockClockwise size={12} /> Cambios
                  </button>
                  <button
                    onClick={() => startEdit(u)}
                    className="border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 flex items-center gap-1"
                    data-testid={`user-edit-${u.id}`}
                    data-help="Cambia nombre, rol, sucursal o reinicia la contraseña de este usuario."
                  >
                    <PencilSimple size={12} /> Editar
                  </button>
                  <button
                    onClick={() => remove(u.id)}
                    className="border border-red-200 text-red-600 px-3 py-1 text-xs hover:bg-red-50 flex items-center gap-1"
                    data-testid={`user-delete-${u.id}`}
                    data-help="Elimina la cuenta del usuario. No podrá iniciar sesión más (pide confirmación)."
                  >
                    <Trash size={12} /> Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", required, disabled, minLength, help }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        disabled={disabled}
        minLength={minLength}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        data-help={help}
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-klein outline-none disabled:bg-zinc-50"
      />
    </div>
  );
}
